import { useState } from "react";
import { auth, db } from "../firebase";
import { collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import toast from "react-hot-toast";

const LINK_TYPES = ["Register link", "Landing page", "Social campaign"];
const AFFILIATE_PROGRAMS = ["Turnover Sharing", "Revenue Sharing"];
const BASE_URL = "https://www.iqcage.com/register?lid=";

export default function LinkModal({ onClose, onSaved }) {
  const [type, setType] = useState(LINK_TYPES[0]);
  const [program, setProgram] = useState(AFFILIATE_PROGRAMS[0]);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [user] = useAuthState(auth);

  const handleSave = async () => {
    if (!user) {
      toast.error("You must be logged in.");
      return;
    }

    setSaving(true);
    try {
      const ref = doc(collection(db, "affiliate_links"));
      await setDoc(ref, {
        userId: user.uid,
        linkType: type,
        program,
        comment,
        createdAt: serverTimestamp(),
        referralLink: `${BASE_URL}${ref.id}`,
      });
      toast.success("Affiliate link created.");
      onSaved?.();
    } catch (err) {
      toast.error(err?.message || "Failed to create link.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-[32px] border border-white/10 bg-[rgba(11,18,31,0.96)] p-6 shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[var(--aff-muted)]">
              New link
            </div>
            <h3 className="mt-2 text-2xl font-black text-white">Create a campaign URL</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-white/10 bg-white/[0.05] px-3 py-2 text-sm text-white"
          >
            Close
          </button>
        </div>

        <div className="mt-6 space-y-4">
          <Field label="Link type">
            <select
              value={type}
              onChange={(event) => setType(event.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white focus:border-emerald-400/40 focus:outline-none"
            >
              {LINK_TYPES.map((value) => (
                <option key={value} value={value} className="bg-slate-900">
                  {value}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Affiliate program">
            <select
              value={program}
              onChange={(event) => setProgram(event.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white focus:border-emerald-400/40 focus:outline-none"
            >
              {AFFILIATE_PROGRAMS.map((value) => (
                <option key={value} value={value} className="bg-slate-900">
                  {value}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Comment">
            <textarea
              rows={3}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Describe the traffic source, ad set, or partner."
              className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white placeholder:text-slate-500 focus:border-emerald-400/40 focus:outline-none"
            />
          </Field>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 text-sm font-semibold text-white"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-2xl bg-[var(--aff-accent)] px-5 py-3 text-sm font-bold text-slate-950"
            disabled={saving}
          >
            {saving ? "Creating..." : "Create link"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-slate-300">{label}</span>
      {children}
    </label>
  );
}
